import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import React, { useState, useCallback, useEffect } from 'react';
import { LoaderIcon, Sparkle, Upload, X } from 'lucide-react';
import WelcomeHero from '../components/WelcomeHero';
import { useLocation, useNavigate } from 'react-router-dom';
import axiosInstance from 'api/axios';
import GetStartedChatInput from '../components/GetStartedChatInput';
import { RootState } from 'store';
import { ChatPayload } from './ChatContainer';
import Questions from 'features/bot-builder/components/Common/Questions';
import { Apps } from 'constants/global';

const DocChatUI = () => {
  const [uploadState, setUploadState] = useState('initial'); // initial, uploading, chat
  const [fileName, setFileName] = useState('');
  const [fileSize, setFileSize] = useState('');
  const [currentStep, setCurrectState] = useState(1); // Define currentStep state
  const location = useLocation();
  const [mainFile, setMainFile] = useState<File | null>(null);
  const [message, setMessage] = useState('');

  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  }, []);

  const handleFileSelect = (file: File) => {
    if (file) {
      // Validate file type
      const fileType = file.name.split('.').pop()?.toLowerCase();
      const validTypes = ['pdf', 'doc', 'docx'];
      
      if (!validTypes.includes(fileType || '')) {
        toast.error('Please upload a PDF, DOC, or DOCX file');
        return;
      }
      
      // Validate file size (20MB limit)
      const maxSize = 20 * 1024 * 1024; // 20MB in bytes
      if (file.size > maxSize) {
        toast.error('File size must be less than 20MB');
        return;
      }
      
      setFileName(file.name);
      setFileSize((file.size / (1024 * 1024)).toFixed(1));
      setUploadState('uploading');
      // Simulate upload progress
      setMainFile(file);
    }
  };

  const suggestions = [
    'What is this document about?',
    'Can you summarize the pdf content?',
    'What are the key takeaways of the document?',
  ];

  const handleAddSuggestions = (index: number) => {
    setMessage(suggestions[index]);
    sendMessage(suggestions[index]);
  };

  const isUrlMode = location.pathname.includes('/from-url');

  const sendMessage = async (content: any) => {
    if (!content.trim()) return;

    // Add user message to chat
    setLoading(true);
    try {
      let payload: ChatPayload = {
        query: content,
      };
      if (!user) {
        payload = {
          ...payload,
          id: localStorage.getItem('fileId'),
        };
      }
      // Make request to your backend API
      const response = await axiosInstance.private.post(
        '/api/v1/chat-with-doc/chat',
        payload
      );
      localStorage.setItem('chat1', content);
      localStorage.setItem('chat2', response.data.data.response);
      navigate('/chat-with-docs/chats');
      if (!response) {
        throw new Error('Failed to get response');
      }
    } catch (error) {
      console.error('Error:', error);
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setUploadState('initial');
  };

  useEffect(() => {
    if (uploadState === 'chat') {
      setCurrectState(2);
    }
  }, [uploadState]);

  const handleStartConversation = async () => {
    setLoading(true);
    const formData = new FormData();
    if (mainFile) {
      formData.append('file', mainFile);
    }

    try {
      if (mainFile) {
        const fileUrl = URL.createObjectURL(mainFile);
        localStorage.setItem('filePath', String(fileUrl));
      }

      const data = await axiosInstance.image.post(
        '/api/v1/chat-with-doc/upload',
        formData
      );

      localStorage.setItem('fileId', data.data.data.id);
      setUploadState('chat');
    } catch (error) {
      toast.error('Please Upload document and Start Converstation');
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen justify-center items-center bg-background">
      <div className="w-full h-full b flex flex-col lg:flex-row justify-center bg-gradient-light dark:bg-gradient-dark bg-no-repeat bg-cover">
        <div className="w-full lg:w-[500px] lg:max-w-[500px] p-4 lg:p-[60px]">
          <WelcomeHero />
        </div>
        <div className="w-full lg:w-3/5 lg:max-w-[850px] p-4 lg:px-[60px] lg:pt-[60px] lg:pb-[72px]">
          {!isUrlMode && (
            <div className="flex sm:flex-row text-text-primary mb-[20px] gap-4 sm:gap-8">
              <div>
                <p
                  className={`text-[12px] sm:text-[14px] font-medium ${currentStep === 1 ? 'text-text-primary ' : 'text-text-secondary '}`}
                >
                  STEP 1
                </p>
                <h3
                  className={`text-[20px] sm:text-[24px] ${currentStep === 1 ? ' text-text-primary font-bold' : 'text-text-secondary '}`}
                >
                  Upload Document
                </h3>
              </div>
              <div>
                <p
                  className={`text-[12px] sm:text-[14px] ${currentStep === 2 ? 'text-text-primary ' : 'text-text-secondary '}`}
                >
                  STEP 2
                </p>
                <h3
                  className={`text-[20px] sm:text-[24px] ${currentStep === 2 ? ' text-text-primary font-bold' : 'text-text-secondary '}`}
                >
                  Ask Questions!
                </h3>
              </div>
            </div>
          )}

          <div className="bg-background rounded-lg p-4 sm:p-10">
            {uploadState === 'initial' && (
              <div
                className="border-2 border-dashed h-[200px] sm:h-[267px] border-primary rounded-lg p-4 sm:p-12 text-center cursor-pointer"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => document.getElementById('fileInput')?.click()}
              >
                <input
                  type="file"
                  id="fileInput"
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      handleFileSelect(e.target.files[0]);
                    }
                  }}
                  accept=".pdf,.doc,.docx"
                />
                <Upload className="mx-auto mb-4 text-primary" size={24} />
                <div className="text-base sm:text-lg mb-2 text-text-primary ">
                  Drag and drop or click to choose document
                </div>
                <div className="text-xs sm:text-sm text-text-secondary ">
                  PDF, DOC, DOCX (UP TO 20MB)
                </div>
              </div>
            )}

            {uploadState === 'uploading' && (
              <div className="p-4 sm:p-8 h-[200px] sm:h-[267px] flex flex-col items-center rounded-sm bg-background-main relative">
                <button
                  onClick={handleCancel}
                  disabled={loading ? true : false}
                  className="absolute top-4 right-4 text-primary p-1"
                >
                  <X size={20} />
                </button>

                <div className="w-12 h-12  rounded-lg flex items-center justify-center mb-6">
                  <img src="/FileDoc.png" alt="file" />
                </div>

                <div className="text-center mb-6">
                  <div className="font-medium text-text-primary text-base sm:text-lg mb-2">
                    {fileName}
                  </div>
                  <div className="text-xs sm:text-sm text-text-secondary space-x-2">
                    <span>PDF</span>
                    <span>•</span>
                    <span>{fileSize}MB</span>
                  </div>
                </div>

                <div className="w-full bg-background-overlay rounded-full h-1">
                  <div className="bg-primary h-1 rounded-full animate-progress"></div>
                </div>
              </div>
            )}

            {uploadState === 'chat' && (
              <div className="p-4">
                <div className="text-lg text-text-primary sm:text-xl font-semibold mb-6">
                  Hello, what can I help you with?
                </div>
                <div className="mb-6">
                  <GetStartedChatInput
                    message={message}
                    setMessage={setMessage}
                    onSend={sendMessage}
                    disabled={false}
                    loading={loading}
                  />
                  <div className="mt-2 flex flex-wrap gap-2">
                    {suggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        className="text-left text-primary text-xs sm:text-[14px] p-2 bg-secondary flex items-center"
                        onClick={() => handleAddSuggestions(index)}
                      >
                        {suggestion} →
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {uploadState !== 'chat' && (
              <button
                className={`w-full py-2 sm:py-3 mt-4 sm:mt-6 text-white rounded-lg transition-colors ${loading ? 'bg-secondary' : 'bg-primary'}`}
                disabled={loading}
                onClick={handleStartConversation}
              >
                {loading ? (
                  <div className="flex justify-center gap-2 sm:gap-4 text-base sm:text-[19px] items-center">
                    <div className="animate-spin">
                      <LoaderIcon size={18} />
                    </div>
                    Starting Conversation
                  </div>
                ) : (
                  <div className="flex justify-center gap-2 sm:gap-4 text-base sm:text-[19px] items-center">
                    <Sparkle size={18} />
                    Start Conversation
                  </div>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
      <Questions appType={Apps.CHAT_WITH_DOCS} />
    </main>
  );
};

export default DocChatUI;
